import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components/macro";

import { CellMeasurementsParentProvider } from "Boards";
import { YEAR_RANGE_CONTEXT } from "CrossFeatureComponents/ExtendedFields/NumberField";
import { AnimatePresence, motion } from "framer-motion";

import FnrLoadingBricks from "UI/FnrLoadingBricks";

import API from "./API";
import { BoardContext } from "./BoardContext";
import { Cell } from "./Cells";
import FiltersPanelAlert from "./FiltersPanelAlert";
import FooterRow from "./FooterRow";
import HeaderRow from "./HeaderRow";
import { TableBody } from "./TableBody";
import TableContent from "./TableContent";
import TableEmptyMessage from "./TableEmptyMessage";
import { transformSaveCellShapeToRowShape } from "./Utils/dataTransformations";
import { COLUMN_TYPES, NAME_ACCESSOR, ROW_TYPES } from "./const";
import useGroupOrSignupID from "./useGroupOrSignupID";

export default function Table() {
  const [{ boardID, columns, filtersPanelOpen, showLoadingOverlay }, dispatch] =
    useContext(BoardContext);
  const groupOrSignupID = useGroupOrSignupID();
  const [measurements, setMeasurements] = useState();

  const headerRowRef = useRef();
  const footerRowRef = useRef();
  const tableDataRef = useRef();
  const innerScrollableRef = useRef();

  useLayoutEffect(() => {
    if (!filtersPanelOpen) getMeasurements();
    else setTimeout(() => getMeasurements(), 250);

    // Aug 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line
  }, [filtersPanelOpen]);

  useEffect(() => {
    if (!measurements || !innerScrollableRef.current) return;

    innerScrollableRef.current.addEventListener("scroll", (e) => {
      if (headerRowRef.current)
        headerRowRef.current.scrollLeft = e.target.scrollLeft;
      if (footerRowRef.current)
        footerRowRef.current.scrollLeft = e.target.scrollLeft;
    });

    headerRowRef.current.addEventListener("scroll", (e) => {
      if (innerScrollableRef.current)
        innerScrollableRef.current.scrollLeft = e.target.scrollLeft;
      if (footerRowRef.current)
        footerRowRef.current.scrollLeft = e.target.scrollLeft;
    });

    footerRowRef.current.addEventListener("scroll", (e) => {
      if (headerRowRef.current)
        headerRowRef.current.scrollLeft = e.target.scrollLeft;
      if (innerScrollableRef.current)
        innerScrollableRef.current.scrollLeft = e.target.scrollLeft;
    });
  }, [measurements]);

  // const memoizedColumns = useMemo(() => columns, [columns]);
  // const memoizedData = useMemo(() => data, [data, dataChanged]);

  const updateCellValue = useCallback(
    ({ entityID, columnID, value }) => {
      const column = columns.find((col) => col.ID === columnID);
      if (!column) return;

      const transformed = transformSaveCellShapeToRowShape(
        column.typeDictID,
        value
      );
      if (typeof transformed === "undefined") return;

      dispatch({
        type: "Set Cell Value",
        payload: { entityID, columnID, value: transformed },
      });
    },
    [columns, dispatch]
  );

  const saveCellValue = useCallback(
    ({
      columnID,
      entityID,
      entityType = "user",
      value,
      origValue,
      localOnly,
    }) => {
      updateCellValue({ entityID, columnID, value });
      if (localOnly) return;

      const filters = window.fnReactBus.getCurrentFilterRequest();

      API.saveData({
        boardID,
        columnID,
        entityID,
        entityType,
        value,
        filters,
        ...groupOrSignupID,
      })
        .post()
        .then(({ rowTotals, total, relatedEntities }) => {
          if (rowTotals) {
            dispatch({ type: "Update rowTotals", payload: rowTotals });
          }
          if (total)
            dispatch({
              type: "Set Column Total",
              payload: { entityID, total: total },
            });
          if (Array.isArray(relatedEntities) && relatedEntities.length > 0) {
            relatedEntities.forEach((relatedEntity) => {
              if (
                relatedEntity.entityType === ROW_TYPES.USER &&
                typeof relatedEntity.entityID === "number" &&
                relatedEntity.entityID > 0
              )
                updateCellValue({
                  entityID: relatedEntity.entityID,
                  columnID,
                  value,
                });
            });
          }
        })
        .catch((err) => {
          updateCellValue({ entityID, columnID, value: origValue }); // reset the value to original
        });
    },
    [dispatch, groupOrSignupID, boardID, updateCellValue]
  );

  const getMeasurements = () => {
    const measurements = tableDataRef.current.getBoundingClientRect();
    if (filtersPanelOpen) measurements.width -= 321;
    setMeasurements(measurements);
  };

  useEffect(() => {
    window.addEventListener("resize", getMeasurements);
    return () => window.removeEventListener("resize", getMeasurements);

    // Aug 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line
  }, []);

  const shouldHideTotalColumn = columns
    // ignore the column without actual data
    .filter(({ accessor }) => accessor !== NAME_ACCESSOR)
    .every(
      ({ typeDictID, extendedFieldData }) =>
        !(
          typeDictID === COLUMN_TYPES.BOOL ||
          (typeDictID === COLUMN_TYPES.NUMBER &&
            extendedFieldData?.rangeContext !== YEAR_RANGE_CONTEXT)
        )
    );

  const rowWidth =
    (columns.length - (shouldHideTotalColumn ? 1 : 0)) * 150 + 200;

  return (
    <CellMeasurementsParentProvider>
      <TableWrapper>
        <TableBody
          className={filtersPanelOpen ? "filters_open" : null}
          ref={tableDataRef}
        >
          <HeaderRow
            ref={headerRowRef}
            shouldHideTotalColumn={shouldHideTotalColumn}
          />

          <TableEmptyMessage />

          {measurements && (
            <TableContent
              rowWidth={rowWidth}
              saveCellValue={saveCellValue}
              innerScrollableRef={innerScrollableRef}
              measurements={measurements}
              shouldHideTotalColumn={shouldHideTotalColumn}
            />
          )}
          <FooterRow
            ref={footerRowRef}
            shouldHideTotalColumn={shouldHideTotalColumn}
          />
        </TableBody>

        <AnimatePresence>
          {showLoadingOverlay ? (
            <LoadingOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <FnrLoadingBricks show size={36} />
            </LoadingOverlay>
          ) : null}
        </AnimatePresence>
      </TableWrapper>

      <FiltersPanelAlert />
    </CellMeasurementsParentProvider>
  );
}

const TableWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  opacity: ${(props) => (props.measured ? 1 : 1)};
`;

export const SpacerCell = styled(Cell)`
  flex-grow: 1;
  flex-shrink: 1;
  width: 1px;
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  background-color: rgba(0, 0, 0, 0.3);
`;
